import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { listLegalEvents, listLegalEventTypes, getLegalDiarySettings, addAgeingNote } from "@/lib/api";
import AddLegalEventDialog from "@/pages/legal/AddLegalEventDialog";

const CLOSED = new Set(["write_off", "closed_review", "fully_paid", "resolved_collections"]);

/** Emma de31b7a3fd: a live legal file cannot be left without a future action in the diary. */
export async function caseNeedsNextAction(caseRow) {
  if (!caseRow || caseRow.legal_closure || CLOSED.has(caseRow.legal_stage || "")) return false;
  const r = await listLegalEvents({ case_id: caseRow.case_id, status: "scheduled", include_notes: false, limit: 5 });
  return (r.events || []).length === 0;
}

export function NextActionGuardDialog({ caseRow, actor, onClose, onLeave }) {
  const [addOpen, setAddOpen] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [diarySettings, setDiarySettings] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const openAdd = async () => {
    try {
      const [t, s] = await Promise.all([listLegalEventTypes(), getLegalDiarySettings()]);
      setEventTypes(t.types || []); setDiarySettings(s);
    } catch { /* dialog still opens with defaults */ }
    setAddOpen(true);
  };

  const leaveAnyway = async () => {
    if (reason.trim().length < 5) { toast.error("Give a short reason — it goes on the case notes"); return; }
    setBusy(true);
    try {
      await addAgeingNote(caseRow.case_id, { text: `Left the legal file with NO next action in the diary. Reason: ${reason.trim()}`, note_type: "note", author: actor || "Ops", audience: "internal" });
      onLeave();
    } catch (e) {
      toast.error("Could not record the reason: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="legal-next-action-guard">
          <DialogHeader>
            <DialogTitle className="text-base">No next action in the diary</DialogTitle>
            <DialogDescription>
              <b>{caseRow.customer_name}</b> ({caseRow.loan_application_id}) has nothing scheduled in the legal calendar. A live legal file must always carry a next action, otherwise it can be lost. Closed files (fully paid / written off / closed for review) are exempt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button className="w-full" onClick={openAdd} data-testid="legal-guard-add-action"><CalendarPlus className="w-4 h-4 mr-1.5" /> Add the next action</Button>
            <div className="text-[11px] text-muted-foreground pt-1">Or leave without one — the reason is written to the case notes:</div>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. awaiting solicitor's advice — will diarise on reply" data-testid="legal-guard-reason" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="legal-guard-stay">Stay on the file</Button>
            <Button variant="ghost" className="text-rose-700" onClick={leaveAnyway} disabled={busy || reason.trim().length < 5} data-testid="legal-guard-leave"><LogOut className="w-4 h-4 mr-1.5" /> Leave anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AddLegalEventDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        eventTypes={eventTypes}
        defaultDate={new Date().toISOString().slice(0, 10)}
        diarySettings={diarySettings}
        caseCourts={caseRow.issuing_courts || null}
        fixedCase={{ case_id: caseRow.case_id, customer_name: caseRow.customer_name, loan_application_id: caseRow.loan_application_id }}
        onCreated={() => { setAddOpen(false); toast.success("Next action diarised"); onLeave(); }}
      />
    </>
  );
}
